/**
 * Canonical Canvas Node Deletion Policy
 * (VISTOERA_CANVAS_GLOBAL_EMPTY_NODE_CLOSE_DIRECT_DELETE_AND_UNIFIED_
 * DELETION_CONFIRMATION_POLICY_P0_V1 — TASK-00096).
 *
 * ONE global rule for every single-node delete entry point (header ×,
 * right-click "删除节点", single-node keyboard Delete/Backspace, spec
 * §1/§2/§29): a node that has never received any meaningful user work
 * is removed immediately — no "删除选中的节点？" confirmation. A node
 * that already carries ANY user-authored content — non-empty text, a
 * bound asset/entity, a valid business edge, a generation result or
 * history, an in-flight/failed run, or a user-changed business
 * parameter — keeps the existing confirmation (spec §4-§27).
 *
 * Multi-node deletion ALWAYS confirms regardless of content (spec
 * §38/§39 "多选是另一种风险等级...不要为了空节点快速删除取消 bulk delete
 * confirmation") — this module never even runs the emptiness check for
 * more than one target at once.
 *
 * `CanvasFunctionCardHeader` and every node component stay completely
 * unaware of any of this — they only ever call
 * `onRequestDelete(nodeId, anchorEl)` (spec §28/§29 "Header 只负责
 * requestDelete(nodeId)"); `CanvasBoard` is the ONLY caller of
 * `getCanvasNodeDeletionPolicy` below.
 *
 * Safety fallback (spec §62 "UNKNOWN → CONFIRM，不要 UNKNOWN → DELETE"):
 * a node type this module cannot confidently classify — not even
 * present in the static Node Registry — is treated as REQUIRING
 * confirmation, never the reverse. Emptiness is only ever a POSITIVE
 * finding produced by a real signal below; it is never the default
 * assumption for anything this module doesn't recognize.
 *
 * Zero extra network requests (spec §52/§53): every signal below reads
 * fields/edges/entity-binding state already held in the live Canvas
 * node/edge graph `CanvasBoard` already loaded — nothing in this
 * module ever fetches assets, history, or run status itself.
 */

import { CANVAS_KNOWN_NODE_TYPES, CANVAS_NODE_DEFAULT_FIELDS } from './canvasNodeDeletionDefaults'
import { nodeFieldsIndicateActiveGeneration } from './generationAssetDestination'
import type { FlowNodeData } from './types'

/** Minimal shape this module needs from a live React Flow node — never
 * the full `@xyflow/react` `Node` type, so this stays testable with
 * plain fixtures and has zero coupling to React Flow internals. */
export interface CanvasDeletionPolicyNode {
  id: string
  type?: string
  data: unknown
}

export interface CanvasDeletionPolicyEdge {
  source: string
  target: string
}

export interface CanvasNodeDeletionPolicyContext {
  /** Reserved for future per-workspace default resolution — not
   * currently consulted (see `canvasNodeDeletionDefaults.ts`'s own doc
   * comment on why generator defaults are hand-mirrored rather than
   * computed from live workspace/provider state). Accepted here so
   * `CanvasBoard` has a stable place to thread it through without a
   * signature change if a future node type's emptiness ever needs it. */
  workspaceId?: string | null
  /** Reserved for future locale-aware default resolution — `language`-
   * style fields are handled generically via
   * `UNIVERSAL_IGNORE_FIELD_KEYS` instead (spec §5 "默认语言...不算用户
   * 操作"), so this is not currently consulted either. */
  locale?: string | null
}

export interface CanvasNodeDeletionPolicyResult {
  requiresConfirmation: boolean
  /** Machine-readable reason — asserted directly by regression tests;
   * never itself shown to the user (spec §30 "本任务主要目标不是重新设计
   * Modal"). */
  reason: string
}

export interface CanvasNodeContentResult {
  meaningful: boolean
  reason: string
}

/** Pure UI/runtime bookkeeping keys that a handful of node types persist
 * INSIDE their own `fields` bag. Most UI state (`collapsed` at the
 * `FlowNodeData` level, pinned/hover/focus/position/selected/dragging)
 * already lives OUTSIDE `fields` entirely per `FlowNodeData`/
 * `CanvasNodeDto` (spec §6/§49) and is never seen by this module in the
 * first place — this list only covers the few UI-only toggles some
 * node types happen to nest INSIDE `fields`. Never counted as
 * meaningful content, even when changed from its own default. */
const UNIVERSAL_IGNORE_FIELD_KEYS = new Set<string>([
  // Duplicate of the top-level `FlowNodeData.collapsed` some node types
  // also echo into their own `fields` bag.
  'collapsed',
  // `generate_creative` / `project_start` "更多创作要求" disclosure toggle.
  'moreRequirementsExpanded',
  // `storyboard_generation` Shot List view-mode tab ('story' | ...).
  'shotListViewMode',
  // `generate_text` — "soft hint for AUTO skill resolution... Not
  // EXPLICIT" per its own doc comment; depends on creation CONTEXT
  // (which upstream node type it was quick-created from), not on
  // anything the user directly authored.
  'preferredSkillKey',
  // Fingerprint/snapshot bookkeeping (spec §50 "runtime metadata") —
  // always redundant with a real result/status field also present
  // whenever any of these are non-null, so ignoring them here never
  // hides real content.
  'generatedFromFingerprint',
  'paramsFingerprintAtGeneration',
  'productionFormatAtGeneration',
  'briefFingerprintAtRun',
  // `generate_video` — spec §5 "默认 Provider...不能因为这些默认字段存在
  // 就认为节点有内容". `GenerateVideoNode`'s own one-time effect
  // (`videoGenerationProvider.ts`'s `resolveDefaultProviderChannelId`
  // doc comment) auto-`patchFields({ providerChannel })`s the Platform
  // Admin's CURRENT canonical default the FIRST time shared provider
  // config loads for a brand-new node — with NO user interaction at
  // all, often well within the same tick a truly untouched node's ×
  // gets clicked. `buildGenerateVideoNodeDefaultFields()` deliberately
  // leaves this key entirely UNSET at creation (see that function's own
  // doc comment), so this key can never appear in `defaults` for a
  // diff — meaning without this ignore-entry ANY brand-new, completely
  // untouched Generate Video node would spuriously require confirmation
  // the instant that background effect fires, which is exactly the
  // "默认 Provider 不算内容" regression this ignore-entry prevents.
  //
  // VistoeraTASK-00253 — `generate_image` now resolves BOTH
  // `providerChannel` AND `modelId` from Platform Admin's live,
  // possibly-changing canonical default execution plan
  // (`imageGenerationProvider.ts`'s `buildGenerateImageNodeDefaultFields`,
  // no longer a fixed `PROVIDER_CHANNEL_BFL`/`'flux-2-pro'` literal, no
  // longer Preview-only) — same "which default execution plan is
  // Admin's CURRENT canonical choice today" bookkeeping as Generate
  // Video's `providerChannel` above, just resolved synchronously at
  // creation instead of by a background effect. `modelId` joins this
  // ignore set for the identical reason: it is the model half of that
  // SAME admin-driven default, never something the user directly typed.
  'providerChannel',
  'modelId',
])

function resolveDefaultFields(nodeType: string): Record<string, unknown> | null {
  return CANVAS_NODE_DEFAULT_FIELDS[nodeType] ?? null
}

function isEmptyish(value: unknown): boolean {
  if (value === undefined || value === null || value === '' || value === false) return true
  if (Array.isArray(value)) return value.length === 0
  if (typeof value === 'object') return Object.keys(value as Record<string, unknown>).length === 0
  return false
}

/** A value judged on its own (no known default to diff against) —
 * conservative in the SAFE direction: only a clearly-non-empty value
 * counts, never a bare truthy number/boolean guess. */
function isMeaningfulValue(value: unknown): boolean {
  if (typeof value === 'string') return value.trim() !== ''
  return !isEmptyish(value)
}

function valuesDiffer(a: unknown, b: unknown): boolean {
  if (a === b) return false
  try {
    return JSON.stringify(a) !== JSON.stringify(b)
  } catch {
    return a !== b
  }
}

/**
 * Generic "has this node's own `fields` bag been meaningfully edited
 * away from its own canonical creation defaults?" — spec §14/§15's
 * reusable dirty-field comparison, built entirely on defaults every
 * node type ALREADY declares for its own creation (no new schema, no
 * per-node hand-written `if` chain).
 *
 * - A string field whose default is `''` uses trim-emptiness (spec §8
 *   "纯空格 trim 后为空 不应触发保护") instead of strict inequality.
 * - An array field whose default is `[]` uses length (covers "选择的
 *   资产" / Generation History / candidate lists generically — spec
 *   §9/§12/§13 — as ONE rule, not one per field name).
 * - Everything else is a deep-equality diff against its OWN declared
 *   default (covers spec §14 "duration 5s→10s" generically, since the
 *   default IS a concrete value like `30` or `'1:1'`).
 * - A key present in `fields` but absent from the resolved defaults
 *   (a genuinely ad-hoc/future field) is judged only by
 *   `isMeaningfulValue` against the implicit empty value for its own
 *   type — i.e. it can only ever raise a false CONFIRM (safe per §62),
 *   never a false immediate-delete.
 * - A key listed in `defaults` but NOT actually present in `fields`
 *   (`Object.prototype.hasOwnProperty` false) is never treated as a
 *   divergence, even if that default value is itself non-emptyish — a
 *   key this node's OWN fields bag never even set cannot be something
 *   the user changed. (`generate_image`'s `providerChannel`/`modelId`
 *   used to rely on exactly this — Preview-only at creation time; as of
 *   VistoeraTASK-00253 both keys are ALWAYS set, in every environment,
 *   to Platform Admin's live canonical default, so they now rely on
 *   `UNIVERSAL_IGNORE_FIELD_KEYS` instead — see that Set's own doc
 *   comment.)
 */
export function fieldsDivergeFromDefaults(
  fields: Record<string, unknown>,
  defaults: Record<string, unknown> | null,
): boolean {
  const defaultKeys = defaults ? Object.keys(defaults) : []
  const fieldKeys = Object.keys(fields)
  const allKeys = new Set<string>([...defaultKeys, ...fieldKeys])
  for (const key of allKeys) {
    if (UNIVERSAL_IGNORE_FIELD_KEYS.has(key)) continue
    if (!Object.prototype.hasOwnProperty.call(fields, key)) continue
    const currentValue = fields[key]
    const hasDefault = !!defaults && Object.prototype.hasOwnProperty.call(defaults, key)
    if (!hasDefault) {
      if (isMeaningfulValue(currentValue)) return true
      continue
    }
    const defaultValue = defaults![key]
    if (isEmptyish(defaultValue)) {
      if (isMeaningfulValue(currentValue)) return true
      continue
    }
    if (valuesDiffer(currentValue, defaultValue)) return true
  }
  return false
}

/**
 * Semantic "does this ONE Canvas node already hold anything a user
 * would not want silently discarded?" (spec §4-§27). Never itself
 * decides confirm/no-confirm (see `getCanvasNodeDeletionPolicy` below)
 * — kept as its own exported primitive per spec §27 so a future caller
 * besides delete (e.g. a "leave canvas with unsaved work" warning)
 * could reuse the exact same semantic without duplicating it.
 */
export function hasMeaningfulCanvasNodeContent(
  node: CanvasDeletionPolicyNode,
  context: { hasBusinessEdge: boolean } & CanvasNodeDeletionPolicyContext,
): CanvasNodeContentResult {
  const data = (node.data || {}) as Partial<FlowNodeData> & { childCount?: number }
  const nodeType = String(data.nodeType || node.type || '')

  // §62 safety fallback — an entirely unrecognized node type (not even
  // present in the static Node Registry) can never be PROVEN empty.
  if (!nodeType || !CANVAS_KNOWN_NODE_TYPES.has(nodeType)) {
    return { meaningful: true, reason: 'unknown_node_type' }
  }

  // §9/§16 — bound canonical asset/entity ("已经选择" an image/video/
  // motion asset, or a legacy entity-family node's own reference),
  // even before any generation has ever run.
  if (data.entityId) {
    return { meaningful: true, reason: 'bound_entity' }
  }

  // §21/§22 — non-empty container/composite (Scene/Shot, or any future
  // container-family node exposing a derived `childCount`).
  if (typeof data.childCount === 'number' && data.childCount > 0) {
    return { meaningful: true, reason: 'has_children' }
  }

  // §10/§11/§34 — any valid business edge touching this node (incoming
  // input OR outgoing workflow connection). `context.hasBusinessEdge`
  // is derived by the caller from the SAME live `edges` array every
  // other Canvas mutation already reads — no separate "is this edge
  // real" classification needed here, because a temporary drag-preview
  // connection never becomes a committed `edges` entry in this codebase
  // (only a real `onConnect` commits one) — §11's caution about
  // drag-preview/orphan edges is a non-issue for this graph model, not
  // something this module has to work around.
  if (context.hasBusinessEdge) {
    return { meaningful: true, reason: 'valid_business_edge' }
  }

  // §24/§51 — a run was ever dispatched for this node, whether it is
  // still in flight or has already finished (success or failure).
  const runId = data.latestRunId
  if (typeof runId === 'string' && runId.trim() !== '') {
    return { meaningful: true, reason: 'has_run' }
  }

  const fields = (data.fields || {}) as Record<string, unknown>

  // §24 — currently generating/queued/submitting (shared helper already
  // used by the pre-existing delete-copy builder — never a second,
  // parallel "is this running" primitive).
  if (nodeFieldsIndicateActiveGeneration(fields)) {
    return { meaningful: true, reason: 'active_generation' }
  }

  // §8/§9/§12/§13/§14/§25 — the reusable default-fields diff. Covers
  // user text input, selected assets/voice templates, generation
  // results, generation history, failed-run error state, and
  // user-changed business parameters, ALL generically, off each node
  // type's own already-canonical creation defaults.
  const defaults = resolveDefaultFields(nodeType)
  if (fieldsDivergeFromDefaults(fields, defaults)) {
    return { meaningful: true, reason: 'fields_diverge_from_defaults' }
  }

  return { meaningful: false, reason: 'empty_untouched' }
}

/**
 * The ONE canonical entry point every delete surface (header ×,
 * right-click "删除节点", single-node keyboard Delete/Backspace) calls
 * — never a per-node `if (...) delete else confirm` (spec §3/§27/§28).
 *
 * `nodeIds.length > 1` ALWAYS requires confirmation (spec §38/§39) —
 * bulk deletion is a separate, deliberately more conservative risk
 * tier the empty-node fast-path never touches, regardless of how many
 * of the selected nodes are individually empty.
 */
export function getCanvasNodeDeletionPolicy(
  nodeIds: readonly string[],
  nodes: readonly CanvasDeletionPolicyNode[],
  edges: readonly CanvasDeletionPolicyEdge[],
  context: CanvasNodeDeletionPolicyContext = {},
): CanvasNodeDeletionPolicyResult {
  if (nodeIds.length === 0) {
    return { requiresConfirmation: false, reason: 'no_target' }
  }
  if (nodeIds.length > 1) {
    return { requiresConfirmation: true, reason: 'multi_select' }
  }
  const [nodeId] = nodeIds
  const node = nodes.find((n) => n.id === nodeId)
  if (!node) {
    // Nothing left to delete — never block on a stale id, but also
    // never claim "empty" about a node this call can't even see.
    return { requiresConfirmation: true, reason: 'node_not_found' }
  }
  const hasBusinessEdge = edges.some((e) => e.source === nodeId || e.target === nodeId)
  const { meaningful, reason } = hasMeaningfulCanvasNodeContent(node, { hasBusinessEdge, ...context })
  return { requiresConfirmation: meaningful, reason }
}
